import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
  Alert,
  InputAdornment,
  IconButton,
  LinearProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  PersonAddAlt1 as PersonAddIcon,
  MarkEmailReadOutlined as MailIcon,
  Google as GoogleIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuthStore } from '../store/authStore';

const MIN_LEN = 12;

// Reglas mínimas (coinciden con el backend: 12+ caracteres, combinando letras y números)
function passwordChecks(pw) {
  return {
    length: pw.length >= MIN_LEN,
    letter: /[a-zA-Z]/.test(pw),
    digit: /[0-9]/.test(pw),
  };
}

const Register = () => {
  const register = useAuthStore((s) => s.register);

  const [form, setForm] = useState({ email: '', password: '', confirm: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const checks = useMemo(() => passwordChecks(form.password), [form.password]);
  const strength = useMemo(() => {
    const ok = Object.values(checks).filter(Boolean).length;
    return (ok / 4) * 100;
  }, [checks]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
      setError('Introduce un correo electrónico válido');
      return;
    }
    if (!Object.values(checks).every(Boolean)) {
      setError(`La contraseña debe tener al menos ${MIN_LEN} caracteres, combinando letras y números`);
      return;
    }
    if (form.password !== form.confirm) {
      setError('Las contraseñas no coinciden');
      return;
    }
    setLoading(true);
    const result = await register({ email: form.email, password: form.password });
    setLoading(false);
    if (result.success) {
      setDone(true);
    } else {
      setError(result.error || 'No se pudo crear la cuenta');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
      <Container maxWidth="sm">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Paper elevation={0} variant="outlined" sx={{ p: 4, borderRadius: 4, bgcolor: 'background.paper' }}>
            {done ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <MailIcon color="primary" sx={{ fontSize: 56, mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  Revisa tu correo
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Enviamos un enlace de verificación a <strong>{form.email}</strong>. Ábrelo para activar tu
                  cuenta y poder iniciar sesión.
                </Typography>
                <Button component={Link} to="/login" variant="contained" size="large">
                  Ir a iniciar sesión
                </Button>
              </Box>
            ) : (
              <>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <PersonAddIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                    Crear cuenta
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Tu correo será tu usuario para iniciar sesión
                  </Typography>
                </Box>

                {error && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                )}

                <form onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    label="Correo electrónico"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={handleChange}
                    margin="normal"
                    required
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Contraseña"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={form.password}
                    onChange={handleChange}
                    margin="normal"
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {form.password.length > 0 && (
                    <Box sx={{ mt: 1, mb: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={strength}
                        color={strength === 100 ? 'success' : strength >= 50 ? 'warning' : 'error'}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {checks.length ? '✓' : '○'} {MIN_LEN}+ caracteres &nbsp;
                        {checks.letter ? '✓' : '○'} letras &nbsp;
                        {checks.digit ? '✓' : '○'} números
                      </Typography>
                    </Box>
                  )}

                  <TextField
                    fullWidth
                    label="Confirmar contraseña"
                    name="confirm"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={form.confirm}
                    onChange={handleChange}
                    margin="normal"
                    required
                    sx={{ mt: 2 }}
                  />

                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    type="submit"
                    disabled={loading}
                    sx={{ py: 1.5, mt: 3, fontSize: '1.05rem', fontWeight: 600 }}
                  >
                    {loading ? 'Creando cuenta...' : 'Crear cuenta'}
                  </Button>
                </form>

                <Divider sx={{ my: 3 }}>
                  <Typography variant="caption" color="text.secondary">o</Typography>
                </Divider>

                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  component="a"
                  href="/api/auth/google/login"
                  startIcon={<GoogleIcon />}
                  sx={{ py: 1.3, fontWeight: 600 }}
                >
                  Continuar con Google
                </Button>

                <Box sx={{ textAlign: 'center', mt: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    ¿Ya tienes cuenta?{' '}
                    <Link to="/login" style={{ textDecoration: 'none' }}>
                      <Typography component="span" variant="body2" color="primary" fontWeight={600}>
                        Inicia sesión
                      </Typography>
                    </Link>
                  </Typography>
                </Box>
              </>
            )}
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Register;
